import * as React from "react";
import { Helpers } from "../../utils/helpers";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import {Config} from "../../config/config";
import axios from "axios";
import {PopupMessage} from "../../utils/message";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export interface AllianceWarSeason {
    id: string;
    name: string;
    status: string;
    configId: number;
    rewardConfigId: number;
    warPool: string[];
    startDate: string;
    endDate: string;
}

export interface AllianceWarParticipant {
    squadId: string;
    name: string;
    member: number;
    maxMember: number;
    participants: any[];
}

export interface AllianceWar {
    id: string;
    seasonId: string;
    attackerMeta: AllianceWarParticipant;
    defenderMeta: AllianceWarParticipant;
    attackerStars: number;
    defenderStars: number;
    status: string;
    result: string;
    startedAt: string;
    endedAt: string;
}

export interface AllianceWarBattleParticipant {
    id: string;
    name: string;
    hqLevel: number;
    warStars: number;
}

export interface AllianceWarBattle {
    id: string;
    allianceWarId: string;
    zoneId: number;
    attackerMeta: AllianceWarBattleParticipant;
    stars: number;
    percent: number;
    status: string;
    squadId: string;
}

export class AllianceWars extends React.Component<any, any> {

    constructor(props) {
        super(props);

        this.state = {
            season: {
                list: [] as AllianceWarSeason[],
                page: 0,
                pageSize: 3,
                hasMore: false,
            },
            war: {
                seasonId: null,
                list: [] as AllianceWar[],
                page: 0,
                pageSize: 3,
                hasMore: false,
            },
            battles: {
                warId: null,
                list: [] as AllianceWarBattle[],
                page: 0,
                pageSize: 10,
            },
            expandedSeasonId: null as string | null,
            expandedWarId: null as string | null,
            loading: false
        };

        document.title = Helpers.documentTitle("Alliance Wars / Seasons");
    }

    componentDidMount = async () => {
        await this.fetchAllianceWarSeasons();
    }

    fetchAllianceWarSeasons = async () => {
        const url = `${Config.getInstance.apiUrl}/api/alliance_wars/seasons`;
        
        this.setState({ loading: true });

        const params = {
            limit: this.state.season.pageSize,
            offset: this.state.season.page * this.state.season.pageSize,
        }

        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        try {
            const response = await axios.get(url, {
                headers,
                params,
            });

            const data = response.data;
            const success = data.success;

            if (success) {
                const seasons = data.seasons || [];
                this.setState({
                    season: { ...this.state.season, list: seasons, hasMore: seasons.length === this.state.season.pageSize },
                });
            } else {
                PopupMessage.error("Failed to fetch alliance war seasons");
            }
        } catch (error) {
            PopupMessage.error("Failed to fetch alliance war seasons");
        } finally {
            this.setState({ loading: false });
        }
    }

    fetchAllianceWars = async (seasonId: string) => {
        const url = `${Config.getInstance.apiUrl}/api/alliance_wars/seasons/${seasonId}`;

        this.setState({ loading: true });

        const params = {
            limit: this.state.war.pageSize,
            offset: this.state.war.page * this.state.war.pageSize,
        }

        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        try {
            const response = await axios.get(url, {
                headers,
                params,
            });

            const data = response.data;
            const success = data.success;

            if (success) {
                const wars = data.wars || [];
                this.setState({
                    war: { ...this.state.war, seasonId, list: wars, hasMore: wars.length === this.state.war.pageSize },
                });
            } else {
                PopupMessage.error("Failed to fetch alliance wars");
            }
        } catch (error) {
            PopupMessage.error("Failed to fetch alliance wars");
        } finally {
            this.setState({ loading: false });
        }
    }

    fetchAllianceWarBattles = async (seasonId: string, warId: string) => {
        const url = `${Config.getInstance.apiUrl}/api/alliance_wars/seasons/${seasonId}/${warId}`;

        this.setState({ loading: true });

        const params = {
            limit: this.state.battles.pageSize,
            offset: this.state.battles.page * this.state.battles.pageSize,
        }

        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        try {
            const response = await axios.get(url, {
                headers,
                params,
            });

            const data = response.data;
            const success = data.success;

            if (success) {
                const battles = data.battles;
                this.setState({ battles: { ...this.state.battles, warId, list: battles } });
            } else {
                PopupMessage.error("Failed to fetch alliance war battles");
            }
        } catch (error) {
            PopupMessage.error("Failed to fetch alliance war battles");
        } finally {
            this.setState({ loading: false });
        }
    }

    onToggleSeason = async (seasonId: string) => {
        const { expandedSeasonId, war } = this.state;
        const isExpanded = expandedSeasonId === seasonId;

        if (isExpanded) {
            this.setState({
                expandedSeasonId: null,
                expandedWarId: null,
            });
            return;
        }

        if (war.seasonId !== seasonId) {
            this.setState({
                war: { ...war, page: 0, seasonId, list: [], hasMore: false },
                battles: { ...this.state.battles, warId: null, list: [] },
            });
            await this.fetchAllianceWars(seasonId);
        }

        this.setState({
            expandedSeasonId: seasonId,
            expandedWarId: null,
        });
    }

    onToggleWar = async (warItem: AllianceWar) => {
        const { expandedWarId, expandedSeasonId, battles } = this.state;
        const isExpanded = expandedWarId === warItem.id;

        if (isExpanded) {
            this.setState({ expandedWarId: null });
            return;
        }

        const seasonId = warItem.seasonId || expandedSeasonId;
        if (seasonId && battles.warId !== warItem.id) {
            await this.fetchAllianceWarBattles(seasonId, warItem.id);
        }

        this.setState({ expandedWarId: warItem.id });
    }

    onSeasonPageChange = async (nextPage: number) => {
        if (nextPage < 0) {
            return;
        }

        await new Promise<void>((resolve) => {
            this.setState((prevState) => ({
                season: { ...prevState.season, page: nextPage },
                expandedSeasonId: null,
                expandedWarId: null,
                war: { ...prevState.war, seasonId: null, list: [], page: 0, hasMore: false },
                battles: { ...prevState.battles, warId: null, list: [] },
            }), () => resolve());
        });

        await this.fetchAllianceWarSeasons();
    }

    onWarPageChange = async (nextPage: number) => {
        const { expandedSeasonId } = this.state;
        if (!expandedSeasonId || nextPage < 0) {
            return;
        }

        await new Promise<void>((resolve) => {
            this.setState((prevState) => ({
                war: { ...prevState.war, page: nextPage },
                expandedWarId: null,
                battles: { ...prevState.battles, warId: null, list: [] },
            }), () => resolve());
        });

        await this.fetchAllianceWars(expandedSeasonId);
    }

    formatDateTime = (dateString?: string) => {
        if (!dateString) {
            return "-";
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return "-";
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hour = String(date.getHours()).padStart(2, "0");
        const minute = String(date.getMinutes()).padStart(2, "0");
        const second = String(date.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hour}-${minute}-${second}`;
    }

    getWarWinnerName = (warItem: AllianceWar) => {
        const result = (warItem.result || "").toLowerCase();

        if (result.includes("attacker")) {
            return warItem.attackerMeta?.name || "Unknown";
        }

        if (result.includes("defender")) {
            return warItem.defenderMeta?.name || "Unknown";
        }

        if (result.includes("draw") || result.includes("tie")) {
            return "Draw";
        }

        if (warItem.attackerStars > warItem.defenderStars) {
            return warItem.attackerMeta?.name || "Unknown";
        }

        if (warItem.defenderStars > warItem.attackerStars) {
            return warItem.defenderMeta?.name || "Unknown";
        }

        return "Draw";
    }

    copyRowData = async (event: React.MouseEvent<HTMLButtonElement>, rowData: any) => {
        event.stopPropagation();

        const text = JSON.stringify(rowData, null, 2);

        try {
            await navigator.clipboard.writeText(text);
            PopupMessage.success("Copied row data");
        } catch (error) {
            PopupMessage.error("Failed to copy row data");
        }
    }

    render() {
        const { season, war, battles, loading, expandedSeasonId, expandedWarId } = this.state;

        if (loading) {
            return <LoadingIndicator classes="" loadingTextPlaceholder="Fetching data" loadingText={true} />;
        }

        return (
            <div className="contentPadding" style={{ width: "100%", color: "#f3f4f6" }}>
                {season.list.length === 0 ? (
                    <div>No season data</div>
                ) : (
                    <>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px" }}>
                            <button disabled={season.page === 0} onClick={() => this.onSeasonPageChange(season.page - 1)}>Prev</button>
                            <span>Season Page {season.page + 1}</span>
                            <button disabled={!season.hasMore} onClick={() => this.onSeasonPageChange(season.page + 1)}>Next</button>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse"}}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #374151", color: "#e5e7eb" }}>Season</th>
                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #374151", color: "#e5e7eb" }}>Status</th>
                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #374151", color: "#e5e7eb" }}>ConfigId</th>
                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #374151", color: "#e5e7eb" }}>Time</th>
                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #374151", color: "#e5e7eb" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                            {season.list.map((seasonItem: AllianceWarSeason) => {
                                const isSeasonExpanded = expandedSeasonId === seasonItem.id;
                                const warsBySeason = war.list.filter((warItem: AllianceWar) => warItem.seasonId === seasonItem.id);

                                return (
                                    <React.Fragment key={seasonItem.id}>
                                        <tr style={{ cursor: "pointer" }} onClick={() => this.onToggleSeason(seasonItem.id)}>
                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                {isSeasonExpanded ? "▼" : "▶"} {seasonItem.name}
                                            </td>
                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>{seasonItem.status || "-"}</td>
                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>{`${seasonItem.configId} - ${seasonItem.rewardConfigId}`}</td>
                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                {this.formatDateTime(seasonItem.startDate)} - {this.formatDateTime(seasonItem.endDate)}
                                            </td>
                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                <ContentCopyIcon
                                                    style={{ cursor: "pointer", fontSize: "18px" }}
                                                    onClick={(event) => this.copyRowData(event as any, seasonItem)}
                                                />
                                            </td>
                                        </tr>

                                        {isSeasonExpanded && (
                                            <tr>
                                                <td colSpan={5} style={{ padding: "8px 8px 8px 28px", background: "#1f2937" }}>
                                                    {warsBySeason.length === 0 ? (
                                                        <div>No wars</div>
                                                    ) : (
                                                        <>
                                                        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px" }}>
                                                            <button disabled={war.page === 0} onClick={() => this.onWarPageChange(war.page - 1)}>Prev</button>
                                                            <span>War Page {war.page + 1}</span>
                                                            <button disabled={!war.hasMore} onClick={() => this.onWarPageChange(war.page + 1)}>Next</button>
                                                        </div>
                                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>War</th>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Status</th>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Score</th>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Winner</th>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Time</th>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {warsBySeason.map((warItem: AllianceWar) => {
                                                                    const isWarExpanded = expandedWarId === warItem.id;
                                                                    const battlesByWar = battles.list.filter((battleItem: AllianceWarBattle) => battleItem.allianceWarId === warItem.id);
                                                                    const warName = `${warItem.attackerMeta?.name || "Unknown"} vs ${warItem.defenderMeta?.name || "Unknown"}`;

                                                                    return (
                                                                        <React.Fragment key={warItem.id}>
                                                                            <tr style={{ cursor: "pointer" }} onClick={() => this.onToggleWar(warItem)}>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>
                                                                                    {isWarExpanded ? "▼" : "▶"} {warName}
                                                                                </td>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>{warItem.status || "-"}</td>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>
                                                                                    {warItem.attackerStars} - {warItem.defenderStars}
                                                                                </td>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>
                                                                                    {this.getWarWinnerName(warItem)}
                                                                                </td>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>
                                                                                    {this.formatDateTime(warItem.startedAt)} - {this.formatDateTime(warItem.endedAt)}
                                                                                </td>
                                                                                <td style={{ padding: "8px", borderBottom: "1px solid #4b5563" }}>
                                                                                    <ContentCopyIcon
                                                                                        style={{ cursor: "pointer", fontSize: "18px" }}
                                                                                        onClick={(event) => this.copyRowData(event as any, warItem)}
                                                                                    />
                                                                                </td>
                                                                            </tr>

                                                                            {isWarExpanded && (
                                                                                <tr>
                                                                                    <td colSpan={6} style={{ padding: "8px 8px 8px 28px", background: "#111827" }}>
                                                                                        {battlesByWar.length === 0 ? (
                                                                                            <div>No battles</div>
                                                                                        ) : (
                                                                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                                                <thead>
                                                                                                    <tr>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Zone</th>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Side</th>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Player</th>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Result</th>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Status</th>
                                                                                                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #4b5563", color: "#e5e7eb" }}>Action</th>
                                                                                                    </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                    {battlesByWar.map((battleItem: AllianceWarBattle) => (
                                                                                                        <tr key={battleItem.zoneId}>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                {battleItem.zoneId}
                                                                                                            </td>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                {battleItem.squadId === warItem.attackerMeta?.squadId ? warItem.attackerMeta.name : warItem.defenderMeta.name}
                                                                                                            </td>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                {battleItem.attackerMeta?.name || "-"}
                                                                                                            </td>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                {battleItem.stars}★ - {battleItem.percent}%
                                                                                                            </td>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                {battleItem.status || "-"}
                                                                                                            </td>
                                                                                                            <td style={{ padding: "8px", borderBottom: "1px solid #374151" }}>
                                                                                                                <ContentCopyIcon
                                                                                                                    style={{ cursor: "pointer", fontSize: "18px" }}
                                                                                                                    onClick={(event) => this.copyRowData(event as any, battleItem)}
                                                                                                                />
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    ))}
                                                                                                </tbody>
                                                                                            </table>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        );
    }
}
